import { captureHandledException } from '@/lib/sentry';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  SurveyPresetTemplateData,
  SurveyTemplateQuestion,
  SurveyQuestionData,
  SurveyQuestionCategoryData,
  getSurveyPresetTemplates,
  saveSurveyPresetTemplate,
  updateSurveyPresetTemplate,
  deleteSurveyPresetTemplate,
  getSurveyQuestions,
  getSurveyQuestionCategories,
} from '@/lib/firebase';
import { useAuth } from '@/contexts/use-auth';
import { getSurveyTemplateSettings, surveyHasTextQuestions } from '@/lib/survey-template-settings';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ClipboardCheck, Plus, Edit, Trash2, ChevronUp, ChevronDown, X } from 'lucide-react';

const formatModified = (ts: any): string => {
  if (!ts) return '-';
  try {
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    captureHandledException(error, "pulse-survey-templates.parse-date");
    return '-';
  }
};

const INPUT_TYPE_LABELS: Record<string, string> = {
  outOfFive: 'Out of 5',
  text: 'Text',
  yesNo: 'Yes / No',
};

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'bimonthly', label: 'Bimonthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'six months', label: 'Six Months' },
  { value: 'annually', label: 'Annually' },
] as const;

interface FormData {
  name: string;
  openingMessage: string;
  selectedQuestions: SurveyTemplateQuestion[];
  frequency: SurveyPresetTemplateData['frequency'];
  displayType: 'inline' | 'wizard';
  commsEnabled: 'email' | 'none';
  status: 'active' | 'disabled';
}

const EMPTY_FORM: FormData = {
  name: '',
  openingMessage: '',
  selectedQuestions: [],
  frequency: 'monthly',
  displayType: 'inline',
  commsEnabled: 'none',
  status: 'active',
};

export default function PulseSurveyTemplatesPage() {
  const [templates, setTemplates] = useState<SurveyPresetTemplateData[]>([]);
  const [questions, setQuestions] = useState<SurveyQuestionData[]>([]);
  const [categories, setCategories] = useState<SurveyQuestionCategoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SurveyPresetTemplateData | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [questionSearch, setQuestionSearch] = useState('');
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesData, questionsData, categoriesData] = await Promise.all([
        getSurveyPresetTemplates(),
        getSurveyQuestions(),
        getSurveyQuestionCategories(),
      ]);
      setTemplates(templatesData);
      setQuestions(questionsData);
      setCategories(categoriesData);
    } catch (error: any) {
      captureHandledException(error, "pulse-survey-templates.load");
      toast({ title: 'Failed to load data', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const getQuestionText = (id: string) => questions.find(q => q.id === id)?.questionText || id;
  const getQuestionInputType = (id: string) => questions.find(q => q.id === id)?.inputType || '';
  const getCategoryName = (id: string) => categories.find(c => c.key === id)?.name || '';

  const openAdd = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setQuestionSearch('');
    setModalOpen(true);
  };

  const openEdit = (t: SurveyPresetTemplateData) => {
    setEditing(t);
    setFormData({
      name: t.name,
      openingMessage: t.openingMessage,
      selectedQuestions: [...(t.questions || [])].sort((a, b) => a.displayOrder - b.displayOrder),
      frequency: t.frequency,
      displayType: t.displayType === 'wizard' ? 'wizard' : 'inline',
      commsEnabled: t.commsEnabled === 'email' ? 'email' : 'none',
      status: t.status,
    });
    setQuestionSearch('');
    setModalOpen(true);
  };

  const addQuestion = (questionId: string) => {
    if (formData.selectedQuestions.some(sq => sq.questionId === questionId)) return;
    const nextOrder = formData.selectedQuestions.length + 1;
    setFormData(prev => ({
      ...prev,
      selectedQuestions: [...prev.selectedQuestions, { questionId, displayOrder: nextOrder }],
    }));
  };

  const removeQuestion = (questionId: string) => {
    const updated = formData.selectedQuestions
      .filter(sq => sq.questionId !== questionId)
      .map((sq, i) => ({ ...sq, displayOrder: i + 1 }));
    setFormData(prev => ({ ...prev, selectedQuestions: updated }));
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    const list = [...formData.selectedQuestions];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= list.length) return;
    [list[index], list[swapIndex]] = [list[swapIndex], list[index]];
    const reordered = list.map((sq, i) => ({ ...sq, displayOrder: i + 1 }));
    setFormData(prev => ({ ...prev, selectedQuestions: reordered }));
  };

  const availableQuestions = questions.filter(
    q => !formData.selectedQuestions.some(sq => sq.questionId === q.id)
      && (questionSearch
        ? q.questionText.toLowerCase().includes(questionSearch.toLowerCase())
          || getCategoryName(q.categoryId).toLowerCase().includes(questionSearch.toLowerCase())
        : true)
  );

  const requiresWizard = surveyHasTextQuestions(formData.selectedQuestions, questions);
  const templateSettings = getSurveyTemplateSettings({
    questions: formData.selectedQuestions,
    displayType: formData.displayType,
    commsEnabled: formData.commsEnabled,
  }, questions);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Name required', description: 'Please enter a template name.', variant: 'destructive' });
      return;
    }
    if (!formData.openingMessage.trim()) {
      toast({ title: 'Opening message required', description: 'Please enter an opening message.', variant: 'destructive' });
      return;
    }
    if (formData.selectedQuestions.length === 0) {
      toast({ title: 'Questions required', description: 'Please add at least one question.', variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);
      const payload: Omit<SurveyPresetTemplateData, 'id' | 'created' | 'modified'> = {
        name: formData.name.trim(),
        openingMessage: formData.openingMessage.trim(),
        questions: formData.selectedQuestions,
        frequency: formData.frequency,
        ...templateSettings,
        status: formData.status,
      };

      if (editing) {
        await updateSurveyPresetTemplate(editing.id!, payload);
        toast({ title: 'Template updated', description: `${payload.name} has been updated.` });
      } else {
        await saveSurveyPresetTemplate(payload);
        toast({ title: 'Template created', description: `${payload.name} has been added.` });
      }
      setModalOpen(false);
      loadData();
    } catch (error: any) {
      captureHandledException(error, "pulse-survey-templates.save");
      toast({ title: 'Failed to save', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t: SurveyPresetTemplateData) => {
    if (!confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try {
      await deleteSurveyPresetTemplate(t.id!);
      toast({ title: 'Template deleted', description: `${t.name} has been deleted.` });
      loadData();
    } catch (error: any) {
      captureHandledException(error, "pulse-survey-templates.delete");
      toast({ title: 'Failed to delete', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="flex-1 p-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <ClipboardCheck className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Survey Templates</h1>
            <p className="text-slate-600">Manage pulse survey preset templates</p>
          </div>
        </div>
        <Button onClick={openAdd} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Add Template</span>
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-600">Loading templates...</p>
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No templates yet</p>
            <Button onClick={openAdd}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Frequency</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Questions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Modified</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {templates.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">{t.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 capitalize">
                        {t.frequency}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {(t.questions || []).length}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          t.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {t.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{formatModified(t.modified)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(t)} className="mr-2">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(t)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>{editing ? 'Edit Template' : 'Add Template'}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-5 mt-4 pr-1">
            {/* Name */}
            <div>
              <Label htmlFor="t-name">Name *</Label>
              <Input
                id="t-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter template name"
              />
            </div>

            {/* Opening Message */}
            <div>
              <Label htmlFor="t-opening">Opening Message *</Label>
              <Textarea
                id="t-opening"
                value={formData.openingMessage}
                onChange={(e) => setFormData(prev => ({ ...prev, openingMessage: e.target.value }))}
                placeholder="Message shown to respondents at the start of the survey"
                rows={3}
              />
            </div>

            {/* Questions Selection */}
            <div>
              <Label>Questions * ({formData.selectedQuestions.length} selected)</Label>

              {/* Selected questions list */}
              {formData.selectedQuestions.length > 0 && (
                <div className="border border-slate-200 rounded-md divide-y divide-slate-100 mb-3 mt-2 max-h-64 overflow-y-auto">
                  {formData.selectedQuestions.map((sq, index) => (
                    <div key={sq.questionId} className="flex items-center gap-2 px-3 py-2">
                      <span className="text-xs text-slate-400 w-5 text-right shrink-0">{sq.displayOrder}.</span>
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex-1 text-sm text-slate-800 min-w-0 truncate cursor-default">{getQuestionText(sq.questionId)}</span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-sm text-wrap">
                            {getQuestionText(sq.questionId)}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <span className="text-xs text-slate-400 bg-slate-100 rounded px-1.5 py-0.5 shrink-0">
                        {INPUT_TYPE_LABELS[getQuestionInputType(sq.questionId)] || getQuestionInputType(sq.questionId)}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          disabled={index === 0}
                          onClick={() => moveQuestion(index, 'up')}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          disabled={index === formData.selectedQuestions.length - 1}
                          onClick={() => moveQuestion(index, 'down')}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          onClick={() => removeQuestion(sq.questionId)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Question search + available list */}
              <div className="border border-slate-200 rounded-md">
                <div className="p-2 border-b border-slate-100">
                  <Input
                    value={questionSearch}
                    onChange={(e) => setQuestionSearch(e.target.value)}
                    placeholder="Search questions to add..."
                    className="h-8 text-sm"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                  {availableQuestions.length === 0 ? (
                    <p className="text-sm text-slate-500 p-3 text-center">
                      {questions.length === 0 ? 'No questions found. Create some questions first.' : 'No more questions to add.'}
                    </p>
                  ) : (
                    availableQuestions.map(q => (
                      <button
                        key={q.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                        onClick={() => addQuestion(q.id!)}
                      >
                        <Plus className="w-3 h-3 text-slate-400 shrink-0" />
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex-1 truncate cursor-default">{q.questionText}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-sm text-wrap">
                              {q.questionText}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <span className="text-xs text-slate-400 bg-slate-100 rounded px-1.5 py-0.5 shrink-0">
                          {INPUT_TYPE_LABELS[q.inputType] || q.inputType}
                        </span>
                        {getCategoryName(q.categoryId) && (
                          <span className="text-xs text-slate-400 shrink-0">— {getCategoryName(q.categoryId)}</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Frequency */}
            <div>
              <Label>Frequency *</Label>
              <Select
                value={formData.frequency}
                onValueChange={(v) => setFormData(prev => ({ ...prev, frequency: v as FormData['frequency'] }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Display Type */}
            <div>
              <Label htmlFor="t-display-type">Display type *</Label>
              <Select
                value={templateSettings.displayType}
                disabled={requiresWizard}
                onValueChange={(v) => setFormData(prev => ({ ...prev, displayType: v as FormData['displayType'] }))}
              >
                <SelectTrigger id="t-display-type" aria-describedby={requiresWizard ? 't-display-type-help' : undefined}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inline">Inline</SelectItem>
                  <SelectItem value="wizard">Wizard</SelectItem>
                </SelectContent>
              </Select>
              {requiresWizard && (
                <p id="t-display-type-help" className="text-sm text-slate-500 mt-1">
                  Surveys with text-entry questions must use wizard display.
                </p>
              )}
            </div>

            {/* Comms Enabled */}
            <div>
              <Label>Comms Enabled *</Label>
              <Select
                value={formData.commsEnabled}
                onValueChange={(v) => setFormData(prev => ({ ...prev, commsEnabled: v as FormData['commsEnabled'] }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div>
              <Label>Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(v) => setFormData(prev => ({ ...prev, status: v as FormData['status'] }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="shrink-0 flex justify-end gap-2 pt-4 border-t border-slate-100 mt-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Template'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
